import { type FC, Fragment, type ReactNode } from "react";
import Parameter from "~/common/components/Parameter";
import { type AliasesProps, getAliases, ProductAliases } from "~/domains/product/components/ProductAliases";
import {
  type FifteenDigitEntry,
  isKITKITProduct,
  isKITProduct,
  isLinearUnit,
  type PackagingInformationEntry,
  type ProductData,
} from "~/domains/product/types/ProductData";
import {
  userIsConsumerDistributor,
  userIsCustomer,
  userIsDistributor,
  userIsPublic,
  userIsRegistered,
} from "~/domains/user/utils/roles";
import { QuantityStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import {
  getCountriesListFromFifteendigit,
  getQtyInStock,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import { PopperTooltip } from "~/common/components/Tooltip";
import { getPackagingLabel } from "~/features/cart/cart/utils/cartUtils";
import { canSeeCountryOrigin } from "~/utils/siteModes";
import { uniqueValues } from "~/common/utils/misc";
import { type PriceData, type WithSurcharge } from "~/domains/pricing/types/PriceData";
import * as PriceUtils from "~/utils/PriceUtils";
import { useTranslation } from "react-i18next";
import InfoIcon from "@mui/icons-material/Info";
import { getTariffNumbers } from "~/utils/productUtils";
import { useProductContext, useProductDescriptorContext } from "~/domains/product/ProductContext";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { Stack, styled } from "~/common/mui";

interface BaseParamProps extends DivAttributes {
  product: ProductData;
  variant?: "vertical" | "horizontal" | "search-page";
  "data-test-id"?: string;
  children?: never;
}

interface BaseParamWithContextProps extends Omit<BaseParamProps, "product"> {
  product?: ProductData;
}

interface BaseParamWithLabelAndContext extends BaseParamWithContextProps {
  label: ReactNode;
}

interface AliasesParameterProps
  extends BaseParamWithContextProps,
    Omit<AliasesProps, "product" | "visibleAliasesLayout"> {
  product?: ProductData;
  showEmpty?: boolean;
}

export const AliasesParameter = ({
  product: external,
  mode = "all",
  packCode,
  maxAliasesVisible,
  showEmpty,
  ...rest
}: AliasesParameterProps) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });

  if (isKITProduct(product)) {
    return null;
  }
  const aliases = getAliases(product, mode, packCode);
  if (aliases.length === 0) {
    if (!showEmpty) {
      return null;
    } else {
      return (
        <Parameter label={t("product.customerId")} {...rest}>
          -
        </Parameter>
      );
    }
  }

  return (
    <Parameter label={t("product.customerId")} {...rest}>
      <ProductAliases product={product} mode={mode} packCode={packCode} maxAliasesVisible={maxAliasesVisible} />
    </Parameter>
  );
};

const isSchaefflerIdVisible = (product: ProductDescriptor) =>
  !userIsPublic() && !userIsRegistered() && !isProductPMA(product);

export const SchaefflerIdParameter = ({
  productDescriptor: external,
  ...rest
}: Omit<BaseParamProps, "product"> & { productDescriptor?: ProductDescriptor }) => {
  const { t } = useTranslation();
  const productDescriptor = useProductDescriptorContext({ productDescriptor: external });
  if (!isSchaefflerIdVisible(productDescriptor) || isKITKITProduct(productDescriptor.base)) {
    return null;
  }

  return (
    <Parameter label={t("product.schaefflerId")} {...rest}>
      {productDescriptor.base.sapMaterialNumber}
    </Parameter>
  );
};

export const OldDesignationParameter = ({ product: external, ...rest }: BaseParamWithLabelAndContext) => {
  const product = useProductContext({ product: external });

  if (!product.oldDesignation || isKITKITProduct(product)) {
    return null;
  }

  return <Parameter {...rest}>{product.oldDesignation}</Parameter>;
};

export const LinearConfigParameter = ({ product: external, ...rest }: BaseParamWithLabelAndContext) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });

  if (!isLinearUnit(product)) {
    return null;
  }

  return (
    <Parameter {...rest}>
      <span>{t("cartPage.guidway.info", { count: Number(product.guideway) })}</span>
      <br />
      <span>{t("cartPage.carriages.info", { count: Number(product.carriages) })}</span>
    </Parameter>
  );
};

interface CountryOfOriginDescriptorParamProps extends Omit<BaseParamProps, "product"> {
  descriptor?: ProductDescriptor;
  packCode?: string;
  showEmpty?: boolean;
  withoutLabel?: boolean;
}

export const CountryOfOriginDescriptorParam = ({
  descriptor: external,
  packCode,
  showEmpty,
  withoutLabel = false,
  ...rest
}: CountryOfOriginDescriptorParamProps) => {
  const { t } = useTranslation();
  const descriptor = useProductDescriptorContext({ productDescriptor: external });

  if (isKITKITProduct(descriptor.base)) {
    return null;
  }

  const countries = getCountriesListFromFifteendigit({ descriptor, packCode });
  const label = t("common.product.countryOfOrigin");

  if (!canSeeCountryOrigin || countries.length === 0) {
    if (!showEmpty) {
      return null;
    } else {
      if (withoutLabel) {
        return null;
      }
      return (
        <Parameter {...rest} label={label}>
          -
        </Parameter>
      );
    }
  }

  const lastIndex = countries.length - 1;

  return (
    <>
      {withoutLabel ? (
        <Stack direction={"row"} gap={0.5} alignItems={"center"}>
          <PublicOutlinedIcon fontSize="small" />
          <div data-test-id={"countryOfOrigin"}>
            {countries.map((country, index) => (
              <Fragment key={country}>
                {t(`country.${country.toLowerCase()}`)}
                {index !== lastIndex && ", "}
              </Fragment>
            ))}
          </div>
        </Stack>
      ) : (
        <Parameter {...rest} label={label}>
          <div data-test-id={"countryOfOrigin"}>
            {countries.map((country, index) => (
              <Fragment key={country}>
                <CountryTooltip
                  tooltipContent={<span className={"f-no-wrap"}>{t(`country.${country.toLowerCase()}`)}</span>}
                >
                  {country}
                </CountryTooltip>
                {index !== lastIndex && ", "}
              </Fragment>
            ))}
          </div>
        </Parameter>
      )}
    </>
  );
};

const CountryTooltip = styled(PopperTooltip)({
  display: "inline",
});

export const GPSDParameter = ({ product, ...rest }: BaseParamProps) => {
  const { t } = useTranslation();

  if (isKITKITProduct(product)) {
    return null;
  }

  if ((!userIsDistributor() && !userIsConsumerDistributor()) || !product.gpsdGroup) {
    return null;
  }

  const label = t("common.product.gpsdGroup");

  return (
    <Parameter {...rest} label={label}>
      <div data-test-id={"gpsd-group"}>{product.gpsdGroup}</div>
    </Parameter>
  );
};

/**
 * Either provide a surcharge value and a percent to use directly, or
 * provicde a PriceData&WithSurcharge object and surcharge will be calculated from it
 * @param value
 * @param percent
 * @param rest
 * @constructor
 */
export const IncludedSurcharge = ({
  value,
  percent,
  calculateFrom,
  ...rest
}: Omit<BaseParamProps, "product"> & {
  value?: PriceData;
  percent?: number;
  calculateFrom?: PriceData & WithSurcharge;
}) => {
  const { t } = useTranslation();

  if (calculateFrom) {
    percent = calculateFrom.surcharge;
    const rawVal = calculateFrom.value - calculateFrom.baseValue;
    if (rawVal > 0) {
      value = {
        value: calculateFrom.value - calculateFrom.baseValue,
        currencyIso: calculateFrom.currencyIso,
      };
    }
  }

  if (!value) {
    return null;
  }

  const label = t("cart.param.includedSurcharge");
  let text;
  if (percent && percent > 0) {
    text = `${PriceUtils.text2(value, "-")} (${percent * 100}%)`;
  } else {
    text = PriceUtils.text2(value, "-");
  }

  return (
    <Parameter {...rest} label={label}>
      <div data-test-id={"included-surcharge"}>{text}</div>
    </Parameter>
  );
};

export const TariffParameter = ({ product: external, ...rest }: BaseParamWithContextProps) => {
  const { t } = useTranslation();
  const product = useProductContext({ product: external });

  if (isKITKITProduct(product)) {
    return null;
  }

  if (!userIsCustomer() || !product.fifteenDigit) {
    return null;
  }

  const label = t("common.product.tariff");

  const tariffNumbers = getTariffNumbers(product);

  if (!tariffNumbers.length) {
    return null;
  }

  return (
    <Parameter {...rest} label={label}>
      <div data-test-id={"tariff"}>{tariffNumbers.join(", ")}</div>
    </Parameter>
  );
};

interface EANProps extends Omit<BaseParamProps, "product"> {
  product: ProductData;
  packCode?: string;
  showEmpty?: boolean;
}

export const EANParameter = ({ product, packCode, showEmpty, ...rest }: EANProps) => {
  const { t } = useTranslation();

  if (isKITKITProduct(product)) {
    return null;
  }

  const label = t("common.product.ean");

  if (!product.fifteenDigit || Object.values(product.fifteenDigit).length === 0) {
    if (!showEmpty) {
      return null;
    } else {
      return (
        <Parameter {...rest} label={label}>
          <div data-test-id={"ean"}>-</div>
        </Parameter>
      );
    }
  }

  let eans: string[] = [];
  // if packCode exists, keeps the only ones that are maching
  if (packCode !== undefined && packCode.trim().length > 0) {
    const packageData: FifteenDigitEntry = product.fifteenDigit[packCode];
    if (packageData?.schaefflerEan) {
      eans.push(packageData?.schaefflerEan);
    }
  } else {
    // no package data, grab all
    eans = uniqueValues(Object.values(product.fifteenDigit).map((digit) => digit.schaefflerEan)) || [];
  }

  if (eans.length === 0) {
    if (!showEmpty) {
      return null;
    } else {
      return (
        <Parameter {...rest} label={label}>
          <div data-test-id={"ean"}>-</div>
        </Parameter>
      );
    }
  }

  return (
    <Parameter {...rest} label={label}>
      <div data-test-id={"ean"}>{eans.join(", ")}</div>
    </Parameter>
  );
};

interface StockAvailability {
  productDescriptor: ProductDescriptor;
  packCode: string;
}

export const StockAvailabilityParameter = ({ productDescriptor, packCode, ...rest }: StockAvailability) => {
  const { t } = useTranslation();

  const label = t("common.product.availability");

  const inStockQty = getQtyInStock(productDescriptor, packCode);

  const showAvailability = !isProductPMA(productDescriptor) && productDescriptor.base.quantityVisible;

  return (
    <Parameter {...rest} label={label}>
      {showAvailability && <QuantityStockIndicator quantity={inStockQty} />}
      {!showAvailability && "-"}
    </Parameter>
  );
};

interface PackagingParameterProps extends Omit<BaseParamProps, "label" | "product"> {
  packagingInfo: PackagingInformationEntry;
  productDescriptor: ProductDescriptor;
}

export const PackagingParameter = ({ packagingInfo, productDescriptor, ...rest }: PackagingParameterProps) => {
  const { t } = useTranslation();

  const packCode = packagingInfo.packCode;

  const text = getPackagingLabel({
    t,
    productDescriptor,
    packSize: packagingInfo.packSize,
    packCode,
  });

  const label = t("quoteDetailsPage.summary.packagingCode");

  return (
    <Parameter label={label} {...rest}>
      <div className="d-flex">
        {text}
        {!!packagingInfo.preferredPackQuantity && packagingInfo.preferredPackQuantity > 0 && (
          <PopperTooltip
            zIndex={1300}
            className="scha__atc-packaging-opts__pack-size__info"
            tooltipContent={
              <div className="f-no-wrap">
                {t("addToCartPopup.prefPackQuantity", { count: packagingInfo.preferredPackQuantity })}
              </div>
            }
          >
            <StyledInfoIcon fontSize={"small"} />
          </PopperTooltip>
        )}
      </div>
    </Parameter>
  );
};

const StyledInfoIcon = styled(InfoIcon)({
  marginLeft: "8px",
  alignSelf: "center",
});

const SearchGrid = styled("div")({
  display: "grid",
  width: "100%",
  gridTemplateColumns: "40% 1fr",
  gap: "8px 32px",
});

export const SearchPageParamContainer: FC<JSX.IntrinsicElements["div"]> = ({ className, ...rest }) => (
  <SearchGrid className={className} {...rest} />
);
